import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

import Tecnicos from "../../static/media/home/tecnicos.webp";
import AsesoriaIcon from "../../static/media/home/MayorMenor.webp";
import ProvicionIcon from "../../static/media/home/Cotizacion.webp";
import LogisticaIcon from "../../static/media/home/Soporte.webp";
import MaterialesIcon from "../../static/media/home/Luz.webp";

const C = {
  dark: "#0C1F2F",
  navy: "#0057A8",
  navyDark: "#003D73",
  gold: "#FFCD00",
  teal: "#5BC8AF",
  white: "#ffffff",
};

const SERVICES_DATA = [
  {
    id: 1,
    title: "Venta Mayor y Menor",
    description:
      "Atención a hogares y constructoras. Soluciones adaptables con precios altamente competitivos.",
    img: AsesoriaIcon,
  },
  {
    id: 2,
    title: "Cotización Rápida",
    description:
      "Cotiza por WhatsApp al instante. Agilizamos todos tus procesos de compra y logística.",
    img: ProvicionIcon,
  },
  {
    id: 3,
    title: "Soporte Post-venta",
    description:
      "Acompañamiento técnico continuo, cambios directos y gestión rápida de garantías de fábrica.",
    img: LogisticaIcon,
  },
  {
    id: 4,
    title: "Iluminación y Control",
    description:
      "Tecnología LED, tableros de distribución y protecciones para modernizar tus espacios.",
    img: MaterialesIcon,
  },
];

const PARTICLES = [
  { top: "10%", left: "15%", size: 8, delay: 0, color: C.gold },
  { top: "75%", left: "-5%", size: 12, delay: 1.5, color: C.teal },
  { top: "25%", left: "90%", size: 6, delay: 0.8, color: C.white },
  { top: "85%", left: "80%", size: 10, delay: 2.2, color: C.gold },
  { top: "50%", left: "5%", size: 5, delay: 3, color: C.white },
];

const container = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
};
const item = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

// Componente Tarjeta de Servicio
const ServiceCard = ({ service }) => (
  <Box
    sx={{
      background: C.white,
      borderRadius: "20px",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      textAlign: "left",
      p: { xs: "20px", md: "24px" },
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      height: "100%",
      boxSizing: "border-box",
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "12px",
        background: "#F5F7FA",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 1.2,
        mb: 2,
      }}
    >
      <Box
        component="img"
        src={service.img}
        alt={service.title}
        sx={{ width: "100%", height: "100%", objectFit: "contain" }}
      />
    </Box>
    <Typography
      sx={{
        color: C.dark,
        fontWeight: 700,
        fontSize: { xs: 15, md: 16 },
        mb: 0.8,
        lineHeight: 1.2,
      }}
    >
      {service.title}
    </Typography>
    <Typography
      sx={{
        color: "#5c6c7a",
        fontSize: { xs: 12.5, md: 13.5 },
        lineHeight: 1.5,
        fontWeight: 500,
      }}
    >
      {service.description}
    </Typography>
  </Box>
);

export default function Services() {
  return (
    <Box
      id="servicios"
      sx={{
        background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyDark} 100%)`,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        pt: { xs: "60px", md: "80px" },
        pb: { xs: "15vw", md: "8vw" },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          width: "100%",
          px: { xs: "20px", sm: "40px", md: "56px" },
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: 6, lg: 6 },
          boxSizing: "border-box",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* LADO IZQUIERDO: IMAGEN */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          sx={{
            width: { xs: "100%", sm: "85%", lg: "42%" },
            position: "relative",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {PARTICLES.map((particle, i) => (
            <Box
              key={i}
              component={motion.div}
              animate={{
                y: [0, -15, 0],
                opacity: [0.3, 0.8, 0.3],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: particle.delay,
              }}
              sx={{
                position: "absolute",
                top: particle.top,
                left: particle.left,
                width: particle.size,
                height: particle.size,
                borderRadius: "50%",
                background: particle.color,
                boxShadow: `0 0 10px ${particle.color}`,
                zIndex: 0,
              }}
            />
          ))}
          <Box
            sx={{
              position: "absolute",
              top: "-12px",
              left: "-12px",
              width: "100%",
              height: "100%",
              borderRadius: "24px",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              zIndex: 0,
            }}
          />
          <Box
            component={motion.img}
            animate={{ y: [-5, 5, -5] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            src={Tecnicos}
            alt="Técnicos ElectricaGRM"
            sx={{
              width: "100%",
              height: "auto",
              maxHeight: { xs: "300px", sm: "360px", lg: "420px" },
              objectFit: "cover",
              borderRadius: "20px",
              zIndex: 1,
              position: "relative",
              boxShadow: "0 20px 40px rgba(0,0,0,0.30)",
            }}
          />
          <Box
            component={motion.div}
            animate={{ y: [5, -5, 5] }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
            sx={{
              position: "absolute",
              bottom: { xs: "-15px", lg: "20px" },
              right: { xs: "10px", lg: "-25px" },
              background: "rgba(12, 31, 47, 0.7)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              p: "12px 16px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              zIndex: 2,
              boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke={C.white}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </Box>
            <Box>
              <Typography
                sx={{
                  color: C.white,
                  fontWeight: 800,
                  fontSize: 13,
                  lineHeight: 1.2,
                }}
              >
                Soporte Experto
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: 10,
                  fontWeight: 500,
                  mt: 0.3,
                }}
              >
                Asesoría 100% real
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* LADO DERECHO: TEXTO Y TARJETAS */}
        <Box
          sx={{
            width: { xs: "100%", lg: "54%" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            sx={{
              textAlign: { xs: "center", lg: "left" },
              mb: { xs: 3, md: 4 },
            }}
          >
            <Typography
              variant="h2"
              sx={{
                color: C.white,
                fontWeight: 750,
                fontSize: { xs: 28, sm: 34, md: 40 },
                letterSpacing: "-.02em",
                lineHeight: 1.1,
                mb: 1.5,
              }}
            >
              Nuestros Servicios
            </Typography>
            <Box
              sx={{
                width: "45px",
                height: "3px",
                background: C.gold,
                mx: { xs: "auto", lg: 0 },
                borderRadius: "2px",
              }}
            />
          </Box>

          {/* VERSIÓN DESKTOP: CUADRÍCULA */}
          <Box
            component={motion.div}
            variants={container}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            sx={{
              display: { xs: "none", md: "grid" },
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "18px",
            }}
          >
            {SERVICES_DATA.map((service) => (
              <motion.div key={service.id} variants={item}>
                <ServiceCard service={service} />
              </motion.div>
            ))}
          </Box>

          {/* VERSIÓN MOBILE: SWIPER */}
          <Box
            sx={{ display: { xs: "block", md: "none" }, width: "100%", pb: 1 }}
          >
            <Swiper
              modules={[Autoplay]}
              loop={true}
              spaceBetween={14}
              breakpoints={{
                0: { slidesPerView: 1.2 },
                450: { slidesPerView: 2 },
              }}
              autoplay={{ delay: 3500, disableOnInteraction: false }}
              style={{ padding: "10px 0" }}
            >
              {SERVICES_DATA.map((service) => (
                <SwiperSlide key={service.id} style={{ height: "auto" }}>
                  <ServiceCard service={service} />
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        </Box>
      </Box>

      {/* CURVA SINUOSA INFERIOR */}
      <Box
        sx={{
          position: "absolute",
          bottom: -1,
          left: 0,
          width: "100%",
          overflow: "hidden",
          lineHeight: 0,
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        <svg
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
          style={{
            display: "block",
            width: "100%",
            height: "8vw",
            minHeight: "50px",
          }}
        >
          <path
            fill={C.white}
            d="M0,192L80,181.3C160,171,320,149,480,160C640,171,800,213,960,208C1120,203,1280,149,1360,122.7L1440,96L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z"
          ></path>
        </svg>
      </Box>
    </Box>
  );
}
